import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon2, Filter, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import AnnonceCard from '../components/AnnonceCard';
import { AnnonceListItem } from '../data/annonces';
import { searchAnnonces } from '../api/services/AnnonceService';
import './Search.css';

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [rccmQuery, setRccmQuery] = useState('');
  const [results, setResults] = useState<AnnonceListItem[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [limitedAccess, setLimitedAccess] = useState(false);
  const [apiMessage, setApiMessage] = useState<string | null>(null);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    setQuery(q);
    doSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const doSearch = async (searchTerm: string = '', rccm: string = '', page: number = 1) => {
    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const params: any = { page, page_size: 20 };
      if (searchTerm) params.search = searchTerm;
      if (rccm) params.rccm = rccm;

      const response = await searchAnnonces(params);
      const data = response.data;

      if (data.success) {
        setResults(data.results || []);
        setTotalCount(data.metadata?.total_count || 0);
        setCurrentPage(data.metadata?.page || 1);
        setTotalPages(data.metadata?.total_pages || 1);
        setLimitedAccess(data.limited_access || false);
        setApiMessage(data.message || null);
      } else {
        setError(data.message || 'Erreur lors de la recherche');
        setResults([]);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erreur de connexion au serveur';
      setError(message);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    } else {
      setSearchParams({});
    }
    doSearch(query.trim(), rccmQuery.trim());
  };

  const handleRccmSearch = () => {
    if (rccmQuery.trim()) {
      doSearch('', rccmQuery.trim());
    }
  };

  const handlePageChange = (page: number) => {
    doSearch(query, rccmQuery, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setQuery('');
    setRccmQuery('');
    setResults([]);
    setHasSearched(false);
    setError(null);
    setSearchParams({});
  };

  return (
    <div className="search-page">
      <div className="search-container">
        <h1 className="search-page-title">Rechercher des annonces</h1>

        <form className="search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Rechercher une entreprise, un mot-clé..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" disabled={isLoading}>
            {isLoading ? <Loader size={18} className="spin" /> : <SearchIcon2 size={18} />}
          </button>
        </form>

        <div className="advanced-panel">
          <button className="advanced-toggle" onClick={() => setAdvancedOpen(!advancedOpen)} type="button">
            <Filter size={16} />
            Recherche avancée
            {advancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {advancedOpen && (
            <div className="advanced-filters">
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Recherche par RCCM</label>
                  <input
                    type="text"
                    placeholder="Ex: CI-ABJ-2024-B-12345"
                    value={rccmQuery}
                    onChange={(e) => setRccmQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="filter-actions">
                <button
                  className="btn-search-main"
                  onClick={handleRccmSearch}
                  type="button"
                  disabled={isLoading}
                >
                  Rechercher par RCCM
                </button>
                <button className="btn-reset" onClick={handleReset} type="button">
                  Réinitialiser
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Messages d'état */}
        {error && (
          <div className="search-error">
            {error}
          </div>
        )}

        {limitedAccess && apiMessage && (
          <div className="search-warning">
            {apiMessage}
          </div>
        )}

        {hasSearched && !isLoading && (
          <div className="results-count">
            {totalCount} résultat(s) trouvé(s)
          </div>
        )}

        {/* Loader */}
        {isLoading && (
          <div className="search-loading">
            <Loader size={32} className="spin" />
            <p>Recherche en cours...</p>
          </div>
        )}

        {/* Résultats */}
        {!isLoading && hasSearched && results.length === 0 && !error && (
          <div className="no-results">Aucune annonce trouvée</div>
        )}

        {!isLoading && results.length > 0 && (
          <>
            <div className="results-grid">
              {results.map((a, index) => (
                <AnnonceCard key={`${a.nom_societe}-${a.date_publication}-${index}`} annonce={a} index={index} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={currentPage <= 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Précédent
                </button>
                <span className="pagination-info">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Search;
